
import { PowerBICourse } from './types';

export const POWERBI_INTERMEDIATE_COURSE: PowerBICourse = {
  id: 'powerbi-intermediate',
  title: 'Power BI Intermediate: Advanced Analytics',
  description: 'Advanced Power BI skills including complex DAX formulas, advanced visualizations, and enterprise deployment strategies.',
  level: 'intermediate',
  totalDuration: '16-18 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Power BI Fundamentals course',
    'Experience creating basic Power BI reports',
    'Understanding of business requirements gathering',
    'Basic knowledge of database concepts'
  ],
  learningOutcomes: [
    'Master complex DAX formulas and calculations',
    'Create advanced visualizations and custom visuals',
    'Implement row-level security and data governance',
    'Design scalable data models for enterprise use',
    'Build automated deployment pipelines',
    'Optimize report performance and user experience'
  ],
  modules: [
    {
      id: 'advanced-dax',
      title: 'Advanced DAX Formulas',
      description: 'Master complex DAX functions, time intelligence, and advanced calculation patterns.',
      content: `
# Advanced DAX Formulas

## DAX Fundamentals Review

Data Analysis Expressions (DAX) is a formula language used in Power BI, Excel Power Pivot, and Analysis Services Tabular models.

### DAX Syntax and Context

#### Row Context
Row context refers to the current row being evaluated in a table during formula calculation.

\`\`\`dax
-- Basic calculated column using row context
Customer Full Name = 
Customers[First Name] & " " & Customers[Last Name]

-- Row context automatically iterates through each row
Revenue = Sales[Quantity] * Sales[Unit Price]
\`\`\`

#### Filter Context
Filter context is the set of filters applied to a calculation, determining which rows are included.

\`\`\`dax
-- Measure using filter context
Total Sales = SUM(Sales[Revenue])

-- Filter context changes based on visual filters
-- When filtered by Year = 2023, only 2023 sales are included
\`\`\`

### Advanced DAX Functions

#### CALCULATE Function
The most powerful DAX function for modifying filter context.

\`\`\`dax
-- Basic CALCULATE syntax
CALCULATE(<expression>, <filter1>, <filter2>, ...)

-- Example: Sales for specific product category
Electronics Sales = 
CALCULATE(
    SUM(Sales[Revenue]),
    Products[Category] = "Electronics"
)

-- Multiple filters
High Value Electronics = 
CALCULATE(
    SUM(Sales[Revenue]),
    Products[Category] = "Electronics",
    Sales[Revenue] > 1000
)

-- Remove filters with ALL
Total Sales All Products = 
CALCULATE(
    SUM(Sales[Revenue]),
    ALL(Products[Category])
)
\`\`\`

#### Time Intelligence Functions

##### Year-over-Year Calculations
\`\`\`dax
-- Previous year sales
Sales PY = 
CALCULATE(
    SUM(Sales[Revenue]),
    SAMEPERIODLASTYEAR(Calendar[Date])
)

-- Year-over-year growth
YoY Growth % = 
DIVIDE(
    [Total Sales] - [Sales PY],
    [Sales PY]
)

-- Year-to-date calculations
Sales YTD = 
CALCULATE(
    SUM(Sales[Revenue]),
    DATESYTD(Calendar[Date])
)
\`\`\`

##### Moving Averages
\`\`\`dax
-- 3-month moving average
Sales 3M Avg = 
AVERAGEX(
    DATESINPERIOD(
        Calendar[Date],
        LASTDATE(Calendar[Date]),
        -3,
        MONTH
    ),
    [Total Sales]
)

-- Rolling 12-month total
Sales R12M = 
CALCULATE(
    SUM(Sales[Revenue]),
    DATESINPERIOD(
        Calendar[Date],
        LASTDATE(Calendar[Date]),
        -12,
        MONTH
    )
)
\`\`\`

#### Advanced Filter Functions

##### FILTER Function
\`\`\`dax
-- Dynamic filtering based on conditions
Top 10 Customers Sales = 
CALCULATE(
    SUM(Sales[Revenue]),
    FILTER(
        VALUES(Customers[CustomerID]),
        RANKX(
            ALL(Customers),
            [Total Sales],
            ,
            DESC
        ) <= 10
    )
)

-- Conditional filtering
High Performing Products = 
CALCULATE(
    COUNTROWS(Products),
    FILTER(
        Products,
        [Total Sales] > [Average Sales]
    )
)
\`\`\`

##### KEEPFILTERS Function
\`\`\`dax
-- Maintain existing filters while adding new ones
Sales with Current Filters = 
CALCULATE(
    SUM(Sales[Revenue]),
    KEEPFILTERS(Products[Category] = "Electronics")
)
\`\`\`

### Advanced Calculation Patterns

#### ABC Analysis (Pareto Analysis)
\`\`\`dax
-- Customer ranking by sales
Customer Rank = 
RANKX(
    ALL(Customers[CustomerID]),
    [Total Sales],
    ,
    DESC,
    DENSE
)

-- Running total percentage
Running Total % = 
VAR CurrentCustomerSales = [Total Sales]
VAR RankingTable = 
    ADDCOLUMNS(
        ALL(Customers[CustomerID]),
        "CustomerSales", [Total Sales]
    )
VAR FilteredTable = 
    FILTER(
        RankingTable,
        [CustomerSales] >= CurrentCustomerSales
    )
VAR RunningTotal = SUMX(FilteredTable, [CustomerSales])
VAR TotalSales = SUMX(RankingTable, [CustomerSales])
RETURN
    DIVIDE(RunningTotal, TotalSales)

-- ABC Classification
ABC Category = 
SWITCH(
    TRUE(),
    [Running Total %] <= 0.8, "A",
    [Running Total %] <= 0.95, "B",
    "C"
)
\`\`\`

#### Cohort Analysis
\`\`\`dax
-- Customer first purchase date
First Purchase Date = 
CALCULATE(
    MIN(Sales[Order Date]),
    ALL(Sales[Order Date])
)

-- Cohort month
Cohort Month = 
FORMAT([First Purchase Date], "YYYY-MM")

-- Months since first purchase
Months Since First Purchase = 
DATEDIFF(
    [First Purchase Date],
    MAX(Sales[Order Date]),
    MONTH
)

-- Cohort retention rate
Retention Rate = 
VAR CohortCustomers = 
    CALCULATE(
        DISTINCTCOUNT(Sales[CustomerID]),
        Sales[Order Date] = [First Purchase Date]
    )
VAR ActiveCustomers = 
    DISTINCTCOUNT(Sales[CustomerID])
RETURN
    DIVIDE(ActiveCustomers, CohortCustomers)
\`\`\`

### Performance Optimization

#### Variables in DAX
\`\`\`dax
-- Use variables to improve performance and readability
Sales Performance = 
VAR CurrentSales = SUM(Sales[Revenue])
VAR PreviousYearSales = 
    CALCULATE(
        SUM(Sales[Revenue]),
        SAMEPERIODLASTYEAR(Calendar[Date])
    )
VAR Growth = CurrentSales - PreviousYearSales
VAR GrowthRate = DIVIDE(Growth, PreviousYearSales)
RETURN
    IF(
        GrowthRate > 0.1,
        "Excellent",
        IF(
            GrowthRate > 0.05,
            "Good",
            "Needs Improvement"
        )
    )
\`\`\`

#### Avoid Calculated Columns When Possible
\`\`\`dax
-- Instead of calculated column:
-- Profit Margin = Sales[Revenue] - Sales[Cost]

-- Use measure:
Profit Margin = 
SUM(Sales[Revenue]) - SUM(Sales[Cost])

-- This is more efficient and respects filter context
\`\`\`

### Error Handling in DAX
\`\`\`dax
-- Handle division by zero
Safe Margin % = 
VAR Revenue = SUM(Sales[Revenue])
VAR Cost = SUM(Sales[Cost])
RETURN
    IF(
        Revenue = 0,
        BLANK(),
        DIVIDE(Revenue - Cost, Revenue)
    )

-- Multiple error conditions
Customer Score = 
VAR TotalOrders = COUNTROWS(Sales)
VAR TotalRevenue = SUM(Sales[Revenue])
RETURN
    SWITCH(
        TRUE(),
        TotalOrders = 0, BLANK(),
        TotalRevenue = 0, 0,
        TotalRevenue / TotalOrders
    )
\`\`\`
      `,
      duration: '3 hours',
      difficulty: 'intermediate',
      objectives: [
        'Master complex DAX functions and context manipulation',
        'Implement time intelligence calculations',
        'Create advanced analytical patterns (ABC, Cohort analysis)',
        'Optimize DAX performance with variables and best practices',
        'Handle errors and edge cases in calculations'
      ]
    }
    // Additional intermediate modules would continue here...
  ]
};
